import { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Plus, Image as ImageIcon, Video, Trash2, Edit, Eye, Upload, 
  GripVertical, X, Calendar, MapPin, RefreshCw, ExternalLink, Save
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sortable Media Item Component
function SortableMediaItem({ media, onView, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: media.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group">
      <img 
        src={media.url} 
        alt={media.filename}
        className="w-full h-32 object-cover rounded-sm"
      />
      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 rounded-sm">
        <button
          {...attributes}
          {...listeners}
          className="h-8 w-8 flex items-center justify-center bg-white/90 rounded-sm cursor-grab active:cursor-grabbing"
        >
          <GripVertical className="h-4 w-4 text-slate-600" />
        </button>
        <Button 
          size="sm" 
          variant="outline" 
          className="h-8 w-8 p-0 bg-white/90 rounded-sm"
          onClick={() => onView(media.url)}
        >
          <Eye className="h-4 w-4" />
        </Button>
        <Button 
          size="sm" 
          variant="outline" 
          className="h-8 w-8 p-0 bg-red-500 text-white border-red-500 rounded-sm"
          onClick={() => onDelete(media.id)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export default function EventsGalleryTab({ token }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [hasOrderChanges, setHasOrderChanges] = useState(false);
  const fileInputRef = useRef(null);

  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    date: '',
    location: '',
    status: 'active'
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/events-gallery`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(res.data.events || []);
    } catch (err) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchEvents();
  }, [token]);

  const handleSaveEvent = async () => {
    if (!eventForm.title) {
      toast.error('Please enter event title');
      return;
    }

    try {
      if (editingEvent) {
        await axios.put(`${API}/admin/events-gallery/${editingEvent.id}`, eventForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Event updated');
      } else {
        await axios.post(`${API}/admin/events-gallery`, eventForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Event created');
      }
      fetchEvents();
      setShowEventModal(false);
      resetEventForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!confirm('Are you sure you want to delete this event and all its media?')) return;
    
    try {
      await axios.delete(`${API}/admin/events-gallery/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event deleted');
      fetchEvents();
    } catch (err) {
      toast.error('Failed to delete event');
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !selectedEvent) return;

    setUploading(true);
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));

    try {
      await axios.post(`${API}/admin/events-gallery/${selectedEvent.id}/media`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(`${files.length} file(s) uploaded`);
      fetchEvents();
      // Refresh selected event
      const updated = await axios.get(`${API}/admin/events-gallery/${selectedEvent.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedEvent(updated.data);
      setHasOrderChanges(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload files');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteMedia = async (eventId, mediaId) => {
    try {
      await axios.delete(`${API}/admin/events-gallery/${eventId}/media/${mediaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Media deleted');
      fetchEvents();
      // Refresh selected event
      const updated = await axios.get(`${API}/admin/events-gallery/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedEvent(updated.data);
    } catch (err) {
      toast.error('Failed to delete media');
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id && selectedEvent) {
      setSelectedEvent(prev => {
        const images = prev.media?.filter(m => m.type === 'image') || [];
        const oldIndex = images.findIndex(m => m.id === active.id);
        const newIndex = images.findIndex(m => m.id === over.id);
        
        if (oldIndex !== -1 && newIndex !== -1) {
          const reorderedImages = arrayMove(images, oldIndex, newIndex);
          const videos = prev.media?.filter(m => m.type === 'video') || [];
          return {
            ...prev,
            media: [...reorderedImages, ...videos]
          };
        }
        return prev;
      });
      setHasOrderChanges(true);
    }
  };

  const saveMediaOrder = async () => {
    if (!selectedEvent || !hasOrderChanges) return;
    
    setSavingOrder(true);
    try {
      const mediaOrder = selectedEvent.media?.map(m => m.id) || [];
      await axios.put(`${API}/admin/events-gallery/${selectedEvent.id}/media-order`, 
        { media_order: mediaOrder },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Media order saved');
      setHasOrderChanges(false);
      fetchEvents();
    } catch (err) {
      toast.error('Failed to save order');
    } finally {
      setSavingOrder(false);
    }
  };

  const resetEventForm = () => {
    setEventForm({ title: '', description: '', date: '', location: '', status: 'active' });
    setEditingEvent(null);
  };

  const openEditEvent = (event) => {
    setEventForm({
      title: event.title,
      description: event.description || '',
      date: event.date || '',
      location: event.location || '',
      status: event.status || 'active'
    });
    setEditingEvent(event);
    setShowEventModal(true);
  };

  const openMediaManager = (event) => {
    setSelectedEvent(event);
    setShowMediaModal(true);
    setHasOrderChanges(false);
  };

  return (
    <div data-testid="events-gallery-tab">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-[#1B2A4A]">Events Gallery Management</h2>
          <p className="text-sm text-slate-500">Manage events, images, and videos. Drag to reorder.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchEvents} className="rounded-sm">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Button 
            size="sm" 
            className="bg-[#C5A572] hover:bg-[#B09260] text-white rounded-sm"
            onClick={() => { resetEventForm(); setShowEventModal(true); }}
          >
            <Plus className="h-4 w-4 mr-1" /> Add Event
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-[#1B2A4A]">{events.length}</p>
            <p className="text-xs text-slate-500">Total Events</p>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-green-700">
              {events.filter(e => e.status === 'active').length}
            </p>
            <p className="text-xs text-green-600">Active Events</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-blue-700">
              {events.reduce((sum, e) => sum + (e.media?.filter(m => m.type === 'image').length || 0), 0)}
            </p>
            <p className="text-xs text-blue-600">Total Images</p>
          </CardContent>
        </Card>
        <Card className="border-purple-200 bg-purple-50">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-purple-700">
              {events.reduce((sum, e) => sum + (e.media?.filter(m => m.type === 'video').length || 0), 0)}
            </p>
            <p className="text-xs text-purple-600">Total Videos</p>
          </CardContent>
        </Card>
      </div>

      {/* Events List */}
      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto text-slate-400" />
          <p className="text-sm text-slate-500 mt-2">Loading events...</p>
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-sm">
          <ImageIcon className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 mb-4">No events created yet</p>
          <Button 
            className="bg-[#C5A572] hover:bg-[#B09260] text-white rounded-sm"
            onClick={() => { resetEventForm(); setShowEventModal(true); }}
          >
            <Plus className="h-4 w-4 mr-1" /> Create First Event
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {events.map((event) => (
            <Card key={event.id} className="border-slate-200 overflow-hidden">
              <div className="h-40 bg-slate-100 relative">
                {event.media?.length > 0 ? (
                  <img 
                    src={event.media[0].url} 
                    alt={event.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon className="h-12 w-12 text-slate-300" />
                  </div>
                )}
                <Badge 
                  className={`absolute top-2 right-2 ${event.status === 'active' ? 'bg-green-500' : 'bg-slate-500'}`}
                >
                  {event.status}
                </Badge>
              </div>
              
              <CardContent className="p-4">
                <h3 className="font-semibold text-[#1B2A4A] mb-1">{event.title}</h3>
                {event.date && (
                  <p className="text-xs text-slate-500 flex items-center gap-1 mb-1">
                    <Calendar className="h-3 w-3" /> {new Date(event.date).toLocaleDateString()}
                  </p>
                )}
                {event.location && (
                  <p className="text-xs text-slate-500 flex items-center gap-1 mb-2">
                    <MapPin className="h-3 w-3" /> {event.location}
                  </p>
                )}
                
                <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
                  <span className="flex items-center gap-1">
                    <ImageIcon className="h-3 w-3" /> {event.media?.filter(m => m.type === 'image').length || 0}
                  </span>
                  <span className="flex items-center gap-1">
                    <Video className="h-3 w-3" /> {event.media?.filter(m => m.type === 'video').length || 0}
                  </span>
                </div>

                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="flex-1 rounded-sm text-xs"
                    onClick={() => openMediaManager(event)}
                  >
                    <ImageIcon className="h-3 w-3 mr-1" /> Manage Media
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="rounded-sm"
                    onClick={() => openEditEvent(event)}
                  >
                    <Edit className="h-3 w-3" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="rounded-sm text-red-500 hover:bg-red-50"
                    onClick={() => handleDeleteEvent(event.id)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Event Modal */}
      <Dialog open={showEventModal} onOpenChange={setShowEventModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingEvent ? 'Edit Event' : 'Create New Event'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Event Title *</Label>
              <Input
                placeholder="e.g., IMCAC Annual Conference 2026"
                value={eventForm.title}
                onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                className="rounded-sm mt-1"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                placeholder="Brief description of the event..."
                value={eventForm.description}
                onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                className="rounded-sm mt-1"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Event Date</Label>
                <Input
                  type="date"
                  value={eventForm.date}
                  onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
                  className="rounded-sm mt-1"
                />
              </div>
              <div>
                <Label>Status</Label>
                <select
                  value={eventForm.status}
                  onChange={(e) => setEventForm({ ...eventForm, status: e.target.value })}
                  className="w-full rounded-sm mt-1 border border-slate-200 p-2 text-sm"
                >
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Location</Label>
              <Input
                placeholder="e.g., Mumbai, India"
                value={eventForm.location}
                onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                className="rounded-sm mt-1"
              />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" className="rounded-sm" onClick={() => setShowEventModal(false)}>
                Cancel
              </Button>
              <Button className="bg-[#1B2A4A] text-white rounded-sm" onClick={handleSaveEvent}>
                {editingEvent ? 'Update Event' : 'Create Event'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Media Manager Modal with Drag & Drop */}
      <Dialog open={showMediaModal} onOpenChange={setShowMediaModal}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5 text-[#C5A572]" />
              Manage Media - {selectedEvent?.title}
            </DialogTitle>
          </DialogHeader>
          
          {selectedEvent && (
            <div className="space-y-4">
              {/* Upload Section */}
              <Card className="border-dashed border-2 border-slate-300 bg-slate-50">
                <CardContent className="p-6 text-center">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    multiple
                    accept="image/*,video/*"
                    className="hidden"
                  />
                  <Upload className="h-10 w-10 text-slate-400 mx-auto mb-3" />
                  <p className="text-sm text-slate-600 mb-3">
                    Upload images (JPG, PNG, WebP) or videos (MP4, MOV)
                  </p>
                  <Button 
                    className="bg-[#C5A572] hover:bg-[#B09260] text-white rounded-sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading ? 'Uploading...' : 'Select Files'}
                  </Button>
                </CardContent>
              </Card>

              {/* Media Tabs with Drag & Drop */}
              <Tabs defaultValue="images">
                <div className="flex items-center justify-between">
                  <TabsList className="bg-slate-100 p-1 rounded-sm">
                    <TabsTrigger value="images" className="rounded-sm data-[state=active]:bg-white">
                      <ImageIcon className="h-4 w-4 mr-1" /> 
                      Images ({selectedEvent.media?.filter(m => m.type === 'image').length || 0})
                    </TabsTrigger>
                    <TabsTrigger value="videos" className="rounded-sm data-[state=active]:bg-white">
                      <Video className="h-4 w-4 mr-1" /> 
                      Videos ({selectedEvent.media?.filter(m => m.type === 'video').length || 0})
                    </TabsTrigger>
                  </TabsList>
                  
                  {hasOrderChanges && (
                    <Button 
                      size="sm" 
                      className="bg-green-600 hover:bg-green-700 text-white rounded-sm"
                      onClick={saveMediaOrder}
                      disabled={savingOrder}
                    >
                      <Save className="h-4 w-4 mr-1" /> 
                      {savingOrder ? 'Saving...' : 'Save Order'}
                    </Button>
                  )}
                </div>

                <TabsContent value="images" className="mt-4">
                  {selectedEvent.media?.filter(m => m.type === 'image').length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <ImageIcon className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                      <p>No images uploaded yet</p>
                    </div>
                  ) : (
                    <>
                      <p className="text-xs text-slate-500 mb-3 flex items-center gap-1">
                        <GripVertical className="h-3 w-3" /> Drag images to reorder them
                      </p>
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext
                          items={selectedEvent.media?.filter(m => m.type === 'image').map(m => m.id) || []}
                          strategy={rectSortingStrategy}
                        >
                          <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                            {selectedEvent.media?.filter(m => m.type === 'image').map((media) => (
                              <SortableMediaItem
                                key={media.id}
                                media={media}
                                onView={(url) => window.open(url, '_blank')}
                                onDelete={(id) => handleDeleteMedia(selectedEvent.id, id)}
                              />
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>
                    </>
                  )}
                </TabsContent>

                <TabsContent value="videos" className="mt-4">
                  {selectedEvent.media?.filter(m => m.type === 'video').length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Video className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                      <p>No videos uploaded yet</p>
                    </div>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-4">
                      {selectedEvent.media?.filter(m => m.type === 'video').map((media) => (
                        <Card key={media.id} className="border-slate-200">
                          <CardContent className="p-4">
                            <video 
                              src={media.url} 
                              className="w-full h-48 object-cover rounded-sm bg-black"
                              controls
                            />
                            <div className="flex items-center justify-between mt-3">
                              <p className="text-sm text-slate-600 truncate flex-1">{media.filename}</p>
                              <div className="flex gap-2">
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="rounded-sm"
                                  onClick={() => window.open(media.url, '_blank')}
                                >
                                  <ExternalLink className="h-3 w-3" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="rounded-sm text-red-500 hover:bg-red-50"
                                  onClick={() => handleDeleteMedia(selectedEvent.id, media.id)}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
